import CloudIcon from '@mui/icons-material/Cloud';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Card, { CardProps } from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import AppBarContext from '../components/BusinessPortalAppBarContext';
import {
  getPrimaryNavigationTarget,
  getSecondaryNavigationTarget,
  parseLocationObject
} from '../utils/navigation-utils';
import { isMongoId } from '../utils/url-utils';

const PREFIX = 'StrategyPage';

const classes = {
  container: `${PREFIX}-container`,
  left: `${PREFIX}-left`,
  card: `${PREFIX}-card`,
  iconImg: `${PREFIX}-iconImg`,
  pageHeader: `${PREFIX}-pageHeader`,
  indicator: `${PREFIX}-indicator`,
  link: `${PREFIX}-link`
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.container}`]: {
    width: '100%',
    height: '75px',
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    float: 'left',
    overflow: 'hidden'
  },

  [`& .${classes.left}`]: {
    marginRight: 'auto'
  },

  [`& .${classes.card}`]: {
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
      boxShadow:
        '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)'
    }
  },

  [`& .${classes.iconImg}`]: {
    maxHeight: '75px',
    maxWidth: '75px'
  },

  [`& .${classes.pageHeader}`]: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4)
  },

  [`& .${classes.indicator}`]: {
    height: theme.spacing(1),
    width: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    display: 'inline-block'
  },

  [`& .${classes.link}`]: {
    textDecoration: 'none'
  }
}));

interface ExpandableCardProps extends React.PropsWithChildren<CardProps> {
  height: number;
}

const ExpandableCard = ({ children, height, ...rest }: ExpandableCardProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const [textOverflows, setTextOverflows] = useState(false);
  const elementRef = useRef<any | null>(null);

  const compareSize = (): void => {
    setExpanded(false);
    if (elementRef && elementRef.current) {
      const compare = elementRef.current.offsetHeight < elementRef.current.scrollHeight;
      setTextOverflows(compare);
    }
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
  }, []);

  useEffect(
    () => (): void => {
      window.removeEventListener('resize', compareSize);
    },
    []
  );

  const cardProps = { ...rest, style: { ...rest.style, height: !expanded ? height : 'unset' } };

  return (
    <Card {...cardProps} sx={{ p: 2 }}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div ref={elementRef} style={{ overflow: 'hidden', flexGrow: 1 }}>
          {children}
        </div>
        <div>
          <Button
            onClick={(): void => setExpanded(!expanded)}
            size="small"
            disableRipple
            style={{ visibility: textOverflows ? 'visible' : 'hidden' }}
          >
            {expanded ? 'Read less' : 'Read more'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

interface Data {
  id: string;
  name: string;
  typename: string;
  rangecolor: string;
  prettyrangevalue: string;
  rangename: string;
  parentId: string;
  description: string;
  location?: string;
  children: Data[];
  informationitems?: Data[];
}

interface StrategyPageParams {
  tenantSymbol: string;
  meaId: string;
}

const StrategyPage = (): JSX.Element => {
  const { params } = useRouteMatch<StrategyPageParams>();
  const { search } = useLocation();
  const [viewdefinition, setViewDefinition] = useState({});
  const { setError } = useErrorContext();
  const { setAppBar, setPeriodSelector, selectedPeriod, getPeriodName } = useContext(AppBarContext);

  const getIcon = (measure: Data) => {
    const location = measure.informationitems
      ?.filter(infoitem => infoitem.name === 'icon')
      .pop()?.location;

    return location ? (
      <img src={location} className={classes.iconImg} alt="Not found" />
    ) : (
      <CloudIcon fontSize="large" />
    );
  };

  useEffect(() => {
    setAppBar(true);
    setPeriodSelector(true);
  }, []);

  const queryString = new URLSearchParams(search);
  const viewdefinitionId = queryString.get('viewdefinitionId');

  const fetchSubMeasures = async (parentIds: Array<string>) => {
    if (parentIds?.length === 0) return [];
    const response = await axios.post('measurehierarchy', {
      parentId: parentIds?.join(),
      recursive: false,
      attributes: `name, typename, rangecolor(period=${getPeriodName()}), prettyrangevalue(period=${getPeriodName()}), rangename(period=${getPeriodName()}), description`,
      sortby: 'level,order,name',
      informationitems: true
    });
    return response.data;
  };

  const { isLoading, error, data } = useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `strategy-${params.meaId}-${getPeriodName()}-${viewdefinitionId}`,
    async () => {
      const parentMeasures = await fetchSubMeasures([params.meaId]);
      const parentIds = parentMeasures.data[0].children?.map((parent: any) => parent.id);
      const subMeasures = await fetchSubMeasures(parentIds);

      // move children from sub measures to related parents
      parentMeasures.data[0].children?.forEach((child: any) => {
        subMeasures.data.forEach((relatedChild: any) => {
          if (child.id === relatedChild.id) child.children = relatedChild.children;
        });
      });

      if (viewdefinitionId && isMongoId(viewdefinitionId)) {
        const viewResponse = await axios.get(`/viewdefinitions/${viewdefinitionId}`);
        if (viewResponse.data.data) setViewDefinition(viewResponse.data.data);
      }

      return parentMeasures;
    },
    {
      // The query will not execute until the selectedPeriod exists
      enabled: !!selectedPeriod
    }
  );

  if (error && error.response) setError(error.response.data);

  const appendPeriod = (target: string): string => {
    if (!target.includes('?')) return `${target}?period=${getPeriodName()}`;
    return target.includes('period=') ? target : `${target}&period=${getPeriodName()}`;
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getPrimaryTarget = (measure: any, viewdefinition: any): string => {
    const defaultTarget = `/measures/${measure.id}/${search}`;

    const target = getPrimaryNavigationTarget(measure, viewdefinition) || defaultTarget;
    return appendPeriod(target);
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getSecondaryTarget = (measure: any, viewdefinition: any): string => {
    const defaultTarget = `/kpilist/${measure.id}/${search}`;

    const target = getSecondaryNavigationTarget(measure, viewdefinition) || defaultTarget;
    return appendPeriod(target);
  };

  const getSubMeasure = (submeasure: Data): JSX.Element => (
    <p key={submeasure.id} data-testid="strategy-card-submeasure">
      <Link
        to={parseLocationObject(
          `/${params.tenantSymbol}${getSecondaryTarget(submeasure, viewdefinition)}`,
          data?.data && Array.isArray(data.data) ? data.data[0].name || null : null
        )}
        style={{ textDecoration: 'none' }}
        data-testid="strategy-card-submeasure-link"
      >
        <Typography variant="caption" color="textPrimary">
          <span
            className={classes.indicator}
            style={{ backgroundColor: `#${submeasure.rangecolor}` }}
          />
          <span>{submeasure.name}</span>
        </Typography>
      </Link>
    </p>
  );

  const getSubMeasures = (submeasures: Data[]): JSX.Element => (
    <>{submeasures.map((submeasure: Data) => getSubMeasure(submeasure))}</>
  );

  const getCard = (measure: Data): JSX.Element => (
    <Grid item xs={12} sm={6} md={3} xl={2} key={measure.id}>
      <ExpandableCard className={classes.card} height={416} data-testid="strategy-card">
        <Link
          to={parseLocationObject(
            `/${params.tenantSymbol}${getPrimaryTarget(measure, viewdefinition)}`,
            data?.data && Array.isArray(data.data) ? data.data[0].name || null : null
          )}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <div className={classes.container}>
            <div className={classes.left}>{getIcon(measure)}</div>
            <div>
              <Typography variant="caption" color="textPrimary" data-testid="strategy-card-value">
                <span
                  className={classes.indicator}
                  style={{ backgroundColor: `#${measure.rangecolor}` }}
                />
                <span>{measure.rangename || measure.prettyrangevalue}</span>
              </Typography>
            </div>
          </div>
          <Typography variant="h6" color="inherit" data-testid="strategy-card-title">
            {measure.name}
          </Typography>
          <Typography variant="body1" color="textPrimary">
            {measure.description}
          </Typography>
        </Link>
        {measure.children && measure.children?.length > 0 ? getSubMeasures(measure.children) : null}
      </ExpandableCard>
    </Grid>
  );

  const noCardData = (): JSX.Element => (
    <Grid item>
      <Typography className="customHeader">No data to display</Typography>
    </Grid>
  );

  const getPage = (): JSX.Element => (
    <div data-testid="strategy-page">
      <Typography
        className={`${classes.pageHeader} customHeader`}
        variant="h4"
        data-testid="strategy-page-title"
      >
        {data?.data && Array.isArray(data.data) ? data.data[0].name || null : null}
      </Typography>
      <Grid container spacing={2}>
        {data?.data && Array.isArray(data.data) && data.data[0]?.children?.length > 0
          ? data.data[0].children?.map(getCard)
          : noCardData()}
      </Grid>
    </div>
  );

  const getLoadingIndicator = (): JSX.Element => (
    <div data-testid="strategy-page-loading" style={{ textAlign: 'center', marginTop: '32px' }}>
      <CircularProgress />
    </div>
  );

  return (
    <StyledContainer maxWidth="xl">
      {(!data && !error) || isLoading ? getLoadingIndicator() : getPage()}
    </StyledContainer>
  );
};

export default StrategyPage;
